import { useEffect, useRef } from 'react';
import Lottie, { type LottieRefCurrentProps } from 'lottie-react';
import { dbgFlag } from '../../lib/debug';
import { tintLottie } from '../../lib/lottieTint';

type Props = {
  animationData: unknown;
  size?: number;
  className?: string;
  /** Fill the parent element instead of using a fixed pixel size. */
  fill?: boolean;
  /** Increment to replay the animation from the first frame (e.g. on tap). */
  playToken?: number;
  loop?: boolean;
  /** Autoplay on mount (combine with loop for a continuous animation). */
  autoplay?: boolean;
  /** Park on the last frame (completed state) instead of the first — for draw-in icons. */
  restAtEnd?: boolean;
  /**
   * Recolor near-black strokes/fills to this hex. Prefer over CSS filters —
   * Safari shifts sepia/hue-rotate chains toward purple.
   */
  color?: string;
};

/**
 * Thin wrapper around lottie-react. The wrapper div carries the size; the Lottie
 * SVG fills it (100%) so it scales cleanly. Replays when `playToken` changes.
 */
export function LottieIcon({
  animationData,
  size = 24,
  className,
  fill = false,
  playToken = 0,
  loop = false,
  autoplay = false,
  restAtEnd = false,
  color,
}: Props) {
  const ref = useRef<LottieRefCurrentProps>(null);
  const data = color ? tintLottie(animationData, color) : animationData;

  // TEMPORARY bisection: render an inert placeholder (no lottie-web instance, no
  // rAF loop, no SVG) so we can test whether many animated Lottie instances are
  // what crashes the iOS WebView when the keyboard opens.
  const lottieDisabled = dbgFlag('nolottie');

  useEffect(() => {
    const inst = ref.current;
    if (!inst) return;
    if (playToken > 0) {
      inst.goToAndPlay(0, true);
    } else if (restAtEnd) {
      const end = (animationData as { op?: number })?.op ?? 0;
      inst.goToAndStop(Math.max(0, end - 1), true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playToken]);

  return (
    <div
      style={fill ? undefined : { width: size, height: size }}
      className={`${fill ? 'h-full w-full' : ''} ${className ?? ''}`}
    >
      {lottieDisabled ? null : (
        <Lottie
          // Remount when tint changes — lottie-react won't swap fill/stroke
          // colors in-place if only animationData identity changes via cache.
          key={color ?? 'default'}
          lottieRef={ref}
          animationData={data}
          loop={loop}
          autoplay={autoplay}
          style={{ width: '100%', height: '100%' }}
        />
      )}
    </div>
  );
}
